// hooks/useRecentlyVisited.ts
import {
	DisplayablePage,
	MAX_RAW_ENTRIES,
	STORAGE_KEY,
	VisitedPage,
	buildDisplayList,
	isExcludedFromWrite,
} from '@/utils/visited-pages'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'

const readRaw = (): VisitedPage[] => {
	if (typeof window === 'undefined') return []
	try {
		const json = window.localStorage.getItem(STORAGE_KEY)
		if (!json) return []
		const parsed = JSON.parse(json)
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

const writeRaw = (entries: VisitedPage[]): void => {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
	} catch {
		// localStorage can throw (e.g. Safari private mode, quota). Silently ignore.
	}
}

const appendRaw = (url: string): void => {
	if (isExcludedFromWrite(url)) return
	const prev = readRaw()
	// Skip if the same URL was just recorded — cheap guard against double-fires
	if (prev.length > 0 && prev[prev.length - 1].url === url) return
	const title = typeof document !== 'undefined' ? document.title : ''
	const entry: VisitedPage = { url, title, visitedAt: Date.now() }
	const next = [...prev, entry]
	const trimmed =
		next.length > MAX_RAW_ENTRIES
			? next.slice(next.length - MAX_RAW_ENTRIES)
			: next
	writeRaw(trimmed)
}

/**
 * Write-only hook. Returns a stable `recordVisit` function.
 * Mount on every page to track visits. No localStorage read, no render-time work.
 */
export const useRecordVisit = (): ((url: string) => void) =>
	useCallback((url: string) => {
		// Defer so document.title has been updated after Next.js route change
		setTimeout(() => appendRaw(url), 0)
	}, [])

/**
 * Read hook. Returns the top-10 displayable visited pages, memoized.
 * Mount only where the list is rendered (homepage).
 */
export const useVisitedPages = (): DisplayablePage[] => {
	const router = useRouter()
	const [raw, setRaw] = useState<VisitedPage[]>([])

	useEffect(() => {
		const refresh = () => setTimeout(() => setRaw(readRaw()), 0)
		refresh()
		router.events.on('routeChangeComplete', refresh)
		return () => {
			router.events.off('routeChangeComplete', refresh)
		}
	}, [router.events])

	return useMemo(
		() => buildDisplayList(raw, router.asPath),
		[raw, router.asPath],
	)
}
